import ko from "knockout";
import _ from "lodash";
import ObservableModel from "util/ObservableModel";
import VisibilityCondition from "models/composer/VisibilityCondition";
import BaseQuestion from "./BaseQuestion";

export default ObservableModel.extend(
    {
        defaults: {
            type: "questionGroup",
            name: "",
            optional: false,
            notes: "",
            weight: 0,
            manualWeight: false,
            defaultAnswersToHighest: false,
            defaultAnswersToNA: false,
            naEnabled: false,
            naSelected: false,
            questions: [],
        },
        modelProperties: {
            questions: BaseQuestion,
            visibilityCondition: VisibilityCondition,
        },
    },
    function questionGroupModel_init() {
        const self = this;

        self.modelId = _.uniqueId("questionGroup");
        let hasFakeId = false;
        if (!self.id) {
            self.id = self.modelId;
            hasFakeId = true;
        }

        self.index = ko.observable(-1);

        self.maxNameLength = ko.observable();

        if (!self.visibilityCondition) {
            self.visibilityCondition = new ko.observable(null);
        }

        self.addVisibilityCondition = function () {
            self.visibilityCondition(new VisibilityCondition());
        };

        self.visibilityCondition.extend({
            validation: {
                validator: function (val) {
                    return !val || val.isValid();
                },
            },
        });

        // Add Knockout-Validation functionality.
        self.name.extend({
            localizedValidator: {
                maxLength: {
                    value: self.maxNameLength,
                    messageKey:
                        "composer.surveys.editor.questionGroup.nameTooLong",
                    messageParams: {
                        maxLength: self.maxNameLength,
                    },
                    onlyIf: function () {
                        return self.maxNameLength() > 0;
                    },
                },
            },
            required: true,
        });

        self.questions.extend({
            localizedValidator: {
                minLength: {
                    value: 1,
                    messageKey:
                        "quality.forms.editor.questionGroup.minQuestions",
                },
            },
            deferred: true,
        });

        self.questionsValidationOptions = {
            // setting this to false triggers a validation immediately (default is true)
            // https://github.com/ericmbarnard/Knockout-Validation/wiki/Configuration
            messagesOnModified: false,
        };

        self.weight.extend({
            number: true,
            required: true,
            max: 100,
            min: 0,
        });
        ko.validation.group(self, { deep: true, live: true });

        self.questionMap = ko.computed(function () {
            return _.reduce(
                self.questions(),
                function (accumulator, question, index) {
                    question.questionGroupIndex(self.index());
                    question.index(index);
                    accumulator[question.id] = question;
                    return accumulator;
                },
                {}
            );
        });

        // Add an unwrap function to remove all unnecessary properties.
        // toIgnore - {object} OR {array}
        //   {object} - An object that maps which properties to ignore based on the type of the model.
        //     The toIgnore key pertaining to this model would be;
        //       'questionGroup': ['properties', 'to', 'ignore']
        //   OR {array} an array of properties to ignore:
        //       ['properties', 'to', 'ignore']
        self.unwrap = function (toIgnore, options) {
            toIgnore = toIgnore || {};
            options = options || {};
            const typeName = self.type();
            let ignore = [
                "errors",
                "isAnyMessageShown",
                "isValid",
                "modelId",
                "unwrap",
                "addQuestion",
                "questions",
                "visibilityCondition",
                "addVisibilityCondition",
                "questionMap",
                "index",
            ];
            if (options.ignoreFakeIds && hasFakeId) {
                ignore.push("id");
            }
            let passToKids = false;
            if (toIgnore instanceof Array) {
                // if toIgnore is an array
                ignore = ignore.concat(toIgnore || []);
            } else {
                // if toIgnore is an object mapping
                ignore = ignore.concat(toIgnore[typeName] || []);
                passToKids = true;
            }
            const data = ObservableModel.unwrap(self, ignore);
            data.questions = _.map(self.questions(), function (submodel) {
                return submodel.unwrap(
                    passToKids ? toIgnore : undefined,
                    options
                );
            });

            if (self.visibilityCondition && self.visibilityCondition()) {
                data.visibilityCondition = self
                    .visibilityCondition()
                    .unwrap(passToKids ? toIgnore : undefined, options);
            }

            return data;
        };
    }
);
